// src/encounter/services/use-save-vitals.ts
// Rail-scoped save hook for pre-encounter vitals editing (PROD-1245). create → POST
// /api/Patients/CreateVitals; update → PUT /api/Patients/updateVitals (lowercase `u`, verbatim —
// .NET binder is case-sensitive). Body = PatientVital[] (Flutter patient_chart_data_source_impl.dart
// :1058-1114). Invalidates the encounter-vitals read (['encounter-vitals', encounterId, patientId])
// so the rail re-renders, and the patient-wide vitals history (['vitals-history', patientId, *]) so
// the growth chart and BMI sparkline pick up the new reading. Transport + cache only; body shape lives in lib/to-vitals-request.ts.
//
// NOTE: deliberately a NEW encounter-scoped hook — src/scheduling/services/use-{create,update}-vitals
// are throwing PROD-1190 cross-module stubs, not wired to the endpoint.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post, put } from '@/shared/api/api-client';
import type { VitalsRequest } from '@/encounter/lib/to-vitals-request';
import { NoteDiscardedError, useNoteWrite } from './note-session';

/**
 * `onError` (PROD-2810): called for EVERY failed save — a mutation-level callback, so a failure is
 * seen even when a later save has already started (react-query keeps only the latest one in the
 * hook's `error`, and clears it while that one is pending). The page uses it to latch a signed-note
 * refusal.
 */
export function useSaveVitals(patientId: string | undefined, encounterId: string | undefined, onError?: (error: Error) => void) {
  const noteWrite = useNoteWrite();
  const qc = useQueryClient();
  return useMutation<unknown, Error, VitalsRequest>({
    // A save refused because the clinician discarded the note is not a failure to report (PROD-3107, Codex P2).
    onError: (error) => {
      if (!(error instanceof NoteDiscardedError)) onError?.(error);
    },
    mutationFn: (req: VitalsRequest) =>
      noteWrite(() =>
        req.mode === 'create'
          ? post<unknown>('/api/Patients/CreateVitals', req.vitals)
          : put<unknown>('/api/Patients/updateVitals', req.vitals),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['encounter-vitals', encounterId, patientId] });
      // The longitudinal history behind the growth chart (and the BMI sparkline) is the SAME
      // readings seen patient-wide — a length/weight/head/BMI just saved here must appear on the
      // chart without a reload (Ahmed, 2026-09-07). Prefix match covers every LOINC's query.
      qc.invalidateQueries({ queryKey: ['vitals-history', patientId] });
    },
  });
}
